import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { ArrowDownToLine, ArrowUpFromLine, Send, Eye, EyeOff, RefreshCw, Shield, AlertCircle } from "lucide-react";
import DepositModal from "@/components/wallet/DepositModal";
import WithdrawModal from "@/components/wallet/WithdrawModal";
import TransferModal from "@/components/wallet/TransferModal";
import WhitelistManager from "@/components/wallet/WhitelistManager";
import HistoryTable from "@/components/wallet/HistoryTable";
import ChainStatus from "@/components/wallet/ChainStatus";

const fmt = (v, decimals = 8) => {
  const n = Number(v || 0);
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};
const fmtUsd = (v) =>
  Number(v || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
const fmtInr = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  });

const DISPLAY_CURRENCY_KEY = "cm_display_currency";

export default function WalletsPage() {
  const [params] = useSearchParams();
  const [balances, setBalances] = useState(null);
  const [totalUsd, setTotalUsd] = useState("0");
  const [totalInr, setTotalInr] = useState("0");
  const [usdtInrRate, setUsdtInrRate] = useState(84);
  const [displayCurrency, setDisplayCurrency] = useState(() => {
    try {
      return localStorage.getItem(DISPLAY_CURRENCY_KEY) || "USD";
    } catch {
      return "USD";
    }
  });
  const [hideZero, setHideZero] = useState(false);
  const [hideValues, setHideValues] = useState(false);
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [openModal, setOpenModal] = useState(null); // {type, asset?}
  const [historyKey, setHistoryKey] = useState(0); // bump to force HistoryTable refresh

  const load = async () => {
    setRefreshing(true);
    setLoadError("");
    try {
      const r = await api.get("/api/wallet/balances");
      setBalances(r.data.balances);
      setTotalUsd(r.data.total_usd);
      setTotalInr(r.data.total_inr || "0");
      if (r.data.usdt_inr_rate) setUsdtInrRate(Number(r.data.usdt_inr_rate));
    } catch (e) {
      setLoadError(e.response?.data?.detail || e.message || "Failed to load wallet");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    api.get("/api/markets/fx/usdt-inr")
      .then((r) => setUsdtInrRate(Number(r.data.rate) || 84))
      .catch(() => {});
    const fx = setInterval(() => {
      api.get("/api/markets/fx/usdt-inr")
        .then((r) => setUsdtInrRate(Number(r.data.rate) || 84))
        .catch(() => {});
    }, 60000);
    return () => {
      clearInterval(t);
      clearInterval(fx);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDisplayCurrency = () => {
    setDisplayCurrency((c) => {
      const next = c === "USD" ? "INR" : "USD";
      try {
        localStorage.setItem(DISPLAY_CURRENCY_KEY, next);
      } catch (_) {}
      return next;
    });
  };

  const displayTotal =
    displayCurrency === "INR"
      ? totalInr || String(Number(totalUsd) * usdtInrRate)
      : totalUsd;

  // Open a modal from URL query (?action=deposit&asset=USDT)
  useEffect(() => {
    const a = params.get("action");
    const asset = params.get("asset");
    if (a === "deposit" || a === "withdraw" || a === "transfer" || a === "whitelist") {
      setOpenModal({ type: a, asset: asset || undefined });
    }
  }, [params]);

  const visible = useMemo(() => {
    if (!balances) return [];
    return balances.filter((b) => {
      if (hideZero && Number(b.total) === 0) return false;
      if (search && !b.asset.toLowerCase().includes(search.toLowerCase()) &&
          !b.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [balances, hideZero, search]);

  const hasHeldFunds = useMemo(() => {
    if (!balances) return false;
    return balances.some((b) => Number(b.held || 0) > 0);
  }, [balances]);

  const onModalClose = (didMutate) => {
    setOpenModal(null);
    if (didMutate) {
      load();
      setHistoryKey((k) => k + 1);
    }
  };

  return (
    <AppShell maxWidth="1240px">
      <div className="space-y-6">
        {/* Header strip */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-[28px] font-bold text-cm-navy tracking-tight" data-testid="wallet-page-title">
              Wallet
            </h1>
            <p className="text-[13px] text-cm-grey mt-1">
              Deposit, withdraw and manage your crypto across multiple chains.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOpenModal({ type: "deposit" })}
              className="cm-btn-deposit"
              data-testid="wallet-action-deposit"
            >
              <ArrowDownToLine size={15} /> Deposit
            </button>
            <button
              onClick={() => setOpenModal({ type: "withdraw" })}
              className="px-4 py-2 rounded-[10px] bg-cm-navy text-white text-[13px] font-semibold hover:opacity-90 inline-flex items-center gap-2"
              data-testid="wallet-action-withdraw"
            >
              <ArrowUpFromLine size={15} /> Withdraw
            </button>
            <button
              onClick={() => setOpenModal({ type: "transfer" })}
              className="px-4 py-2 rounded-[10px] border border-[var(--cm-border)] text-cm-navy text-[13px] font-semibold hover:bg-cm-surface inline-flex items-center gap-2"
              data-testid="wallet-action-transfer"
            >
              <Send size={15} /> Send
            </button>
            <button
              onClick={() => setOpenModal({ type: "whitelist" })}
              className="px-4 py-2 rounded-[10px] border border-[var(--cm-border)] text-cm-navy text-[13px] font-semibold hover:bg-cm-surface inline-flex items-center gap-2"
              data-testid="wallet-action-whitelist"
            >
              <Shield size={15} /> Whitelist
            </button>
          </div>
        </div>

        {loadError && (
          <div
            className="flex items-start gap-2 p-3 rounded-[10px] bg-red-50 border border-red-200 text-red-700 text-[13px]"
            data-testid="wallet-load-error"
          >
            <span className="font-semibold">Couldn't refresh wallet:</span>
            <span className="opacity-90">{loadError}</span>
            <button
              onClick={load}
              className="ml-auto text-red-700 underline text-[12px] font-semibold"
            >
              Retry
            </button>
          </div>
        )}

        {/* Warning banner for held funds */}
        {hasHeldFunds && (
          <div
            className="flex items-center justify-between gap-4 p-4 rounded-[12px] bg-amber-50 border border-amber-200 text-amber-900 text-[13px]"
            data-testid="wallet-held-funds-banner"
          >
            <div className="flex items-start gap-2.5">
              <AlertCircle className="text-amber-600 mt-0.5 shrink-0" size={16} />
              <div>
                <span className="font-semibold">Held Funds Alert:</span> Some of your deposits are currently held because the deposit addresses are not whitelisted. Whitelist them to release the funds.
              </div>
            </div>
            <button
              onClick={() => setOpenModal({ type: "whitelist" })}
              className="px-3.5 py-1.5 rounded-[8px] bg-amber-600 hover:bg-amber-700 text-white text-[12px] font-semibold shrink-0"
            >
              Manage Whitelist
            </button>
          </div>
        )}

        {/* Total balance card */}
        <div
          className="rounded-[18px] p-7 text-white relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #001CBF 0%, #022CED 50%, #0029FF 100%)",
          }}
          data-testid="wallet-total-card"
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 80%, white 0%, transparent 25%), radial-gradient(circle at 80% 20%, white 0%, transparent 25%)",
            }}
          />
          <div className="relative flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-wider opacity-80 flex items-center gap-2">
                Total Estimated Balance
                <button
                  type="button"
                  onClick={toggleDisplayCurrency}
                  className="px-2 py-0.5 rounded-md bg-white/15 hover:bg-white/25 text-[11px] font-bold"
                  data-testid="wallet-currency-toggle"
                >
                  {displayCurrency}
                </button>
                <button
                  onClick={() => setHideValues((v) => !v)}
                  className="opacity-70 hover:opacity-100"
                  data-testid="wallet-toggle-values"
                  aria-label="Toggle values"
                >
                  {hideValues ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <div className="text-[40px] font-extrabold tracking-tight mt-1" data-testid="wallet-total-usd">
                {hideValues ? "••••••" : displayCurrency === "INR" ? fmtInr(displayTotal) : fmtUsd(displayTotal)}
              </div>
              <div className="text-[12px] opacity-75 mt-1">
                {displayCurrency === "INR"
                  ? `≈ ${hideValues ? "••••" : fmtUsd(totalUsd)} USD · 1 USDT ≈ ₹${usdtInrRate.toLocaleString("en-IN")}`
                  : `≈ ${hideValues ? "••••" : fmtInr(totalInr || Number(totalUsd) * usdtInrRate)} INR`}
                {" · "}
                {balances?.length || 0} assets · 7 supported chains
              </div>
            </div>
            <button
              onClick={load}
              className="px-3 py-1.5 rounded-[10px] bg-white/15 hover:bg-white/25 text-[12px] font-semibold inline-flex items-center gap-2 backdrop-blur"
              disabled={refreshing}
              data-testid="wallet-refresh"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        <ChainStatus />

        {/* Balance table */}
        <div className="bg-white rounded-[16px] border border-[var(--cm-border)]">
          <div className="px-5 py-4 flex flex-wrap items-center gap-3 border-b border-[var(--cm-border)]">
            <h2 className="text-[15px] font-bold text-cm-navy">Assets</h2>
            <div className="flex-1" />
            <input
              type="text"
              placeholder="Search asset…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-1.5 rounded-[8px] border border-[var(--cm-border)] text-[12px] w-44"
              data-testid="wallet-search"
            />
            <label className="text-[12px] text-cm-grey inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={hideZero}
                onChange={(e) => setHideZero(e.target.checked)}
                data-testid="wallet-hide-zero"
              />
              Hide 0 balances
            </label>
          </div>

          {balances === null ? (
            <div className="px-5 py-12 text-center text-cm-grey">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="px-5 py-12 text-center text-cm-grey">No assets match your filter.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="text-cm-grey text-left">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Asset</th>
                    <th className="px-5 py-3 font-semibold">Networks</th>
                    <th className="px-5 py-3 font-semibold text-right">Available</th>
                    <th className="px-5 py-3 font-semibold text-right">
                      <span className="group relative cursor-help border-b border-dotted border-cm-grey">
                        Held
                        <span className="absolute bottom-full left-1/2 z-10 hidden -translate-x-1/2 mb-1 w-48 rounded bg-cm-navy p-2 text-center text-[11px] font-normal text-white shadow-md group-hover:block whitespace-normal">
                          Deposits pending address whitelisting. Whitelist the deposit address to release.
                        </span>
                      </span>
                    </th>
                    <th className="px-5 py-3 font-semibold text-right">Locked</th>
                    <th className="px-5 py-3 font-semibold text-right">Total</th>
                    <th className="px-5 py-3 font-semibold text-right">USD Value</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--cm-border)]">
                  {visible.map((a) => (
                    <tr key={a.asset} data-testid={`wallet-row-${a.asset}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-9 h-9 rounded-full grid place-items-center text-white text-[13px] font-bold"
                            style={{ backgroundColor: a.icon_color || "#022CED" }}
                          >
                            {a.asset[0]}
                          </div>
                          <div>
                            <div className="text-cm-navy font-semibold">{a.asset}</div>
                            <div className="text-[11px] text-cm-grey">{a.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(a.networks || []).map((n) => (
                            <span key={n} className="px-2 py-0.5 rounded-full bg-cm-surface text-cm-grey text-[11px] font-medium inline-flex items-center gap-1">
                              {n}
                              {n === "BEP20" && (
                                <span className="bg-emerald-100 text-emerald-700 px-1 rounded-sm text-[9px] uppercase font-bold tracking-wider">Testnet</span>
                              )}
                            </span>
                          ))}
                          {a.is_fiat && (
                            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">FIAT</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right text-cm-navy font-semibold tabular-nums" data-testid={`wallet-available-${a.asset}`}>
                        {hideValues ? "••••" : fmt(a.available, a.decimals)}
                      </td>
                      <td className="px-5 py-4 text-right text-cm-grey tabular-nums" data-testid={`wallet-held-${a.asset}`}>
                        {hideValues ? "••••" : fmt(a.held, a.decimals)}
                      </td>
                      <td className="px-5 py-4 text-right text-cm-grey tabular-nums" data-testid={`wallet-locked-${a.asset}`}>
                        {hideValues ? "••••" : fmt(a.locked, a.decimals)}
                      </td>
                      <td className="px-5 py-4 text-right text-cm-navy font-semibold tabular-nums" data-testid={`wallet-total-${a.asset}`}>
                        {hideValues ? "••••" : fmt(a.total, a.decimals)}
                      </td>
                      <td className="px-5 py-4 text-right text-cm-grey tabular-nums">
                        {hideValues ? "••••" : fmtUsd(a.usd_value)}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex gap-1">
                          {!a.is_fiat && (
                            <>
                              <button
                                type="button"
                                onClick={() => setOpenModal({ type: "deposit", asset: a.asset })}
                                className="px-3 py-1 rounded-md text-[12px] font-semibold text-cm-blue hover:bg-blue-50 active:bg-blue-100"
                                data-testid={`wallet-row-deposit-${a.asset}`}
                              >
                                Deposit
                              </button>
                              <button
                                onClick={() => setOpenModal({ type: "withdraw", asset: a.asset })}
                                className="px-3 py-1 rounded-md text-[12px] font-semibold text-cm-navy hover:bg-cm-surface"
                                data-testid={`wallet-row-withdraw-${a.asset}`}
                              >
                                Withdraw
                              </button>
                            </>
                          )}
                          {a.is_fiat && (
                            <span className="px-3 py-1 text-[11px] text-cm-grey italic">via Phase D</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* History */}
        <HistoryTable key={historyKey} />
      </div>

      {/* Modals */}
      {openModal?.type === "deposit" && (
        <DepositModal initialAsset={openModal.asset} onClose={onModalClose} />
      )}
      {openModal?.type === "withdraw" && (
        <WithdrawModal initialAsset={openModal.asset} onClose={onModalClose} />
      )}
      {openModal?.type === "transfer" && (
        <TransferModal initialAsset={openModal.asset} onClose={onModalClose} />
      )}
      {openModal?.type === "whitelist" && (
        <WhitelistManager onClose={onModalClose} />
      )}
    </AppShell>
  );
}
